import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, GripVertical, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { getAdminLanding, updateAdminLanding } from "../../lib/api";
import type { LandingContent, LandingSection } from "../../types/content";
import { LoadingState } from "../../components/Ui";
import { AdminHeader, Field, Notice, Toggle } from "./AdminUi";

const labels: Record<keyof Omit<LandingContent, "seo">, string> = {
  hero: "Hero",
  about: "About",
  products: "Products",
  philosophy: "Philosophy",
  capabilities: "Capabilities",
  statistics: "Statistics",
  cta: "Final CTA",
  footer: "Footer",
};

export function LandingEditor() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["admin-landing"], queryFn: getAdminLanding });
  const [content, setContent] = useState<LandingContent | null>(null);
  const [active, setActive] = useState<keyof Omit<LandingContent, "seo">>("hero");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    if (query.data) setContent(query.data);
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: () => updateAdminLanding(content!),
    onSuccess: (data) => {
      setContent(data);
      setNotice("Landing page saved and published.");
      queryClient.invalidateQueries({ queryKey: ["public-landing"] });
    },
  });

  if (query.isLoading || !content) return <LoadingState label="Loading landing page editor" />;

  const section = content[active] as LandingSection & { email?: string; copyright?: string };
  function patch(values: Partial<typeof section>) {
    setContent((current) => current ? { ...current, [active]: { ...current[active], ...values } } : current);
  }

  return (
    <>
      <AdminHeader
        title="Landing page"
        description="Edit every section shown on the public Steria House website."
        action={
          <div className="admin-actions">
            <a className="button button-secondary" href="/" target="_blank" rel="noreferrer">
              Preview <Eye size={16} />
            </a>
            <button className="button button-primary" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
              {mutation.isPending ? "Saving..." : "Save changes"} <Save size={16} />
            </button>
          </div>
        }
      />
      {notice && <Notice type="success" message={notice} onClose={() => setNotice("")} />}
      {mutation.isError && <Notice type="error" message="Could not save the landing page." onClose={() => mutation.reset()} />}
      <div className="editor-layout">
        <aside className="editor-sections">
          <div className="editor-sections-label">Page sections</div>
          {(Object.keys(labels) as Array<keyof typeof labels>)
            .sort((a, b) => content[a].displayOrder - content[b].displayOrder)
            .map((key) => (
              <button key={key} className={active === key ? "is-active" : ""} onClick={() => setActive(key)}>
                <GripVertical size={15} />
                <span>{labels[key]}</span>
                <i className={content[key].isVisible ? "is-live" : ""} />
              </button>
            ))}
        </aside>
        <section className="admin-card editor-card">
          <div className="card-heading editor-card-heading">
            <div><h2>{labels[active]} section</h2><p>Public content and display preferences</p></div>
            <Toggle checked={section.isVisible} onChange={(checked) => patch({ isVisible: checked })} label="Visible" />
          </div>
          <div className="form-grid">
            <Field label="Eyebrow / badge">
              <input value={section.eyebrow || ""} onChange={(event) => patch({ eyebrow: event.target.value })} />
            </Field>
            <Field label="Display order">
              <input type="number" min={1} value={section.displayOrder} onChange={(event) => patch({ displayOrder: Number(event.target.value) })} />
            </Field>
            <Field label="Section title">
              <input value={section.title} onChange={(event) => patch({ title: event.target.value })} />
            </Field>
            <Field label="Subtitle">
              <input value={section.subtitle || ""} onChange={(event) => patch({ subtitle: event.target.value })} />
            </Field>
            <Field label="Description">
              <textarea rows={5} value={section.description} onChange={(event) => patch({ description: event.target.value })} />
            </Field>
            <Field label="Image URL" hint="Optional. Use a public HTTPS image URL.">
              <input type="url" value={section.imageUrl || ""} onChange={(event) => patch({ imageUrl: event.target.value })} />
            </Field>
            <Field label="Primary button text">
              <input value={section.primaryCtaText || ""} onChange={(event) => patch({ primaryCtaText: event.target.value })} />
            </Field>
            <Field label="Primary button URL">
              <input value={section.primaryCtaUrl || ""} onChange={(event) => patch({ primaryCtaUrl: event.target.value })} />
            </Field>
            <Field label="Secondary button text">
              <input value={section.secondaryCtaText || ""} onChange={(event) => patch({ secondaryCtaText: event.target.value })} />
            </Field>
            <Field label="Secondary button URL">
              <input value={section.secondaryCtaUrl || ""} onChange={(event) => patch({ secondaryCtaUrl: event.target.value })} />
            </Field>
            {active === "footer" && (
              <>
                <Field label="Contact email">
                  <input type="email" value={section.email || ""} onChange={(event) => patch({ email: event.target.value })} />
                </Field>
                <Field label="Copyright">
                  <input value={section.copyright || ""} onChange={(event) => patch({ copyright: event.target.value })} />
                </Field>
              </>
            )}
          </div>
        </section>
      </div>
    </>
  );
}
